import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetch } from "./api";
import OtpInput from "./OtpInput";

type Method = "totp" | "email" | "backup";

/**
 * Verifies a 2FA challenge for the logged-in user and hands back a short-lived
 * twoFactorToken. Drop it in front of any sensitive action (password change,
 * disabling 2FA, regenerating backup codes).
 */
const TwoFactorChallenge: React.FC<{
  methods: Method[];
  title?: string;
  onVerified: (twoFactorToken: string) => void;
  onCancel: () => void;
}> = ({ methods, title, onVerified, onCancel }) => {
  const { t } = useTranslation();
  const labels: Record<Method, string> = {
    totp: t("twofaChallenge.methodTotp"),
    email: t("twofaChallenge.methodEmail"),
    backup: t("twofaChallenge.methodBackup"),
  };
  const heading = title ?? t("twofaChallenge.title");
  const [method, setMethod] = useState<Method>(methods[0] || "email");
  const [code, setCode] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const sendEmailCode = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await apiFetch("/api/2fa/challenge/send-email", { method: "POST" });
      const data = await res!.json();
      setEmailSent(true);
      if (data.devCode) setError(t("twofaChallenge.devModeCode", { code: data.devCode }));
    } catch {
      setError(t("twofaChallenge.couldNotSendCode"));
    }
    setLoading(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/2fa/verify", {
        method: "POST",
        body: JSON.stringify({ method, code: code.trim() }),
      });
      const data = await res!.json();
      if (!res!.ok || !data.twoFactorToken) {
        setError(data.error || t("twofaChallenge.invalidCode"));
        setLoading(false);
        return;
      }
      onVerified(data.twoFactorToken);
    } catch {
      setError(t("twofaChallenge.verificationFailed"));
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm space-y-4">
        <h3 className="text-lg font-bold text-gray-900">{heading}</h3>

        {methods.length > 1 && (
          <div className="flex gap-2 flex-wrap">
            {methods.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMethod(m);
                  setCode("");
                  setError("");
                  setEmailSent(false);
                }}
                className={`text-xs px-3 py-1 rounded-full border ${
                  method === m ? "bg-teal-600 text-white border-teal-600" : "bg-white text-gray-700"
                }`}
              >
                {labels[m]}
              </button>
            ))}
          </div>
        )}

        {method === "email" && !emailSent ? (
          <button
            type="button"
            onClick={sendEmailCode}
            disabled={loading}
            className="w-full bg-teal-600 text-white py-2 rounded font-semibold disabled:opacity-50"
          >
            {loading ? t("twofaChallenge.sending") : t("twofaChallenge.sendEmailCode")}
          </button>
        ) : (
          <>
            <p className="text-sm text-gray-600">
              {method === "totp" && t("twofaChallenge.enterTotp")}
              {method === "email" && t("twofaChallenge.enterEmailCode")}
              {method === "backup" && t("twofaChallenge.enterBackupCode")}
            </p>
            {method === "backup" ? (
              <input
                autoFocus
                className="w-full border rounded p-2 tracking-widest text-center uppercase"
                placeholder="XXXXX-XXXXX"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            ) : (
              <OtpInput value={code} onChange={setCode} onComplete={() => {}} disabled={loading} />
            )}
          </>
        )}

        {error && <div className="text-sm text-center text-red-600">{error}</div>}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-2 rounded border font-semibold text-gray-700"
          >
            {t("twofaChallenge.cancel")}
          </button>
          <button
            type="submit"
            disabled={loading || !code.trim() || (method === "email" && !emailSent)}
            className="flex-1 bg-green-600 text-white py-2 rounded font-semibold disabled:opacity-50"
          >
            {loading ? t("twofaChallenge.verifying") : t("twofaChallenge.verify")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TwoFactorChallenge;
